import { useAuth, getClaims } from '@workos-inc/authkit-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { fetchMe, MeFetchError, type MeResponse } from '../api/me';
import { orgSlugFromClaims } from '../lib/orgSlug';
import { isOrgAdmin } from '../lib/permissions';
import { resolveProductSections, type ProductSections } from '../lib/productSections';
import { productIsProvisioned, provisioningStatusUrl, type SetupWatch } from '../lib/provisioning';
import { isCloudhouseHost } from '../lib/routing';
import { useGuardianReadiness } from '../lib/useGuardianReadiness';
import { useHostReady } from '../lib/useHostReady';
import { INTERIM_READY_MS, useInterimReady } from '../lib/useInterimReady';
import { clearSetupWatch, loadSetupWatch, saveSetupWatch } from '../lib/setupWatchStore';
import { EnterpriseHubView } from './enterprise-hub/EnterpriseHubView';
import { useTrialIntentAutoStart } from '../hooks/useTrialIntentAutoStart';
import { TrialIntentNotice } from '../components/TrialIntentNotice';
import { WaitingRequestsBanner } from '../components/WaitingRequestsBanner';
import { Center, Stack } from '@mantine/core';
import { SectionHeader } from '../components/SectionHeader';

export type State =
  | { kind: 'loading' }
  | { kind: 'error'; status: number }
  | { kind: 'ready'; data: MeResponse };

export type HubOutletContext = {
  state: State;
  sections: ProductSections | null;
  refetch: () => void;
  isOrgAdmin: boolean;
  orgSlug: string | null;
  roles: readonly string[] | null;
  setup: SetupWatch;
  // A website trial link's automatic start is running; the manual Try buttons hold meanwhile.
  trialIntentBusy: boolean;
  // A manual Try started this product's trial, which answers a pending trial link too.
  trialStarted: (productKey: string) => void;
};

function orgHostToGate(settingUp: { productKey: string } | null, orgSlug: string | null): string | null {
  if (!settingUp || !productIsProvisioned(settingUp.productKey) || !orgSlug) return null;
  return isCloudhouseHost(globalThis.location.hostname) ? `${orgSlug}.cloudhouse.com` : null;
}

function gateOnHost(gateHost: string | null, hostReady: ReturnType<typeof useHostReady>): SetupWatch['state'] {
  if (gateHost === null || hostReady === 'ready' || hostReady === 'recovered') return 'ready';
  return hostReady === 'timed-out' || hostReady === 'stopped' ? 'timed-out' : 'checking';
}

export function EnterpriseHub() {
  const auth = useAuth();
  const [state, setState] = useState<State>({ kind: 'loading' });
  // org_slug lives in a custom claim useAuth does not surface; read it off the same
  // access token we fetch for /me so org-hosted product links open on the org's host.
  const [orgSlug, setOrgSlug] = useState<string | null>(null);

  // We deliberately exclude `auth` from the dep array because some AuthKit
  // providers produce a new auth object identity on every render, which would
  // otherwise re-fire this effect, abort the in-flight request, and oscillate state.
  const load = useCallback(() => {
    const ctrl = new AbortController();
    void (async () => {
      // Keep ready data on screen while revalidating — a refetch must not
      // unmount the live scenario (it holds trial banners and busy state).
      setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));
      try {
        const token = await auth.getAccessToken();
        if (!token) {
          setState({ kind: 'error', status: 0 });
          return;
        }
        setOrgSlug(orgSlugFromClaims(getClaims(token)));
        const data = await fetchMe(token, ctrl.signal);
        setState({ kind: 'ready', data });
      } catch (e) {
        if (e instanceof MeFetchError) setState({ kind: 'error', status: e.status });
        else if ((e as Error).name !== 'AbortError') setState({ kind: 'error', status: 0 });
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => load(), [load]);

  // The setup watch lives here, not in a scenario: a first trial starts in
  // NoEntitlementScenario and the refetch swaps the org to MultiAppScenario,
  // so scenario-local state would be lost mid-build. The status URL is derived on
  // every render — never stored — so a watch that outlives a reload picks up the
  // real poll URL the moment the org slug (and any registered builder) is available.
  const [settingUp, setSettingUp] = useState<{ productKey: string; startedAt: number } | null>(null);
  const watchStatusUrl = settingUp ? provisioningStatusUrl(settingUp.productKey, orgSlug) : null;
  const readiness = useGuardianReadiness(watchStatusUrl, {
    getToken: async () => (await auth.getAccessToken()) ?? null,
  });
  const orgId = auth.organizationId ?? null;
  const watch = useCallback(
    (productKey: string) => {
      const startedAt = Date.now();
      if (orgId && productIsProvisioned(productKey)) saveSetupWatch({ orgId, productKey, startedAt });
      setSettingUp({ productKey, startedAt });
    },
    [orgId],
  );
  const dismiss = useCallback(() => {
    clearSetupWatch();
    setSettingUp(null);
  }, []);

  // A reload must not lose a build in progress — without this, the card offers Open
  // into a half-built instance the moment the page comes back. Stale watches (past the
  // wait, with elapsed clamped so clock skew reads as zero) are dropped so an old visit
  // can't resurrect a green banner; a watch belonging to a different org on this browser
  // is left alone, never shown.
  useEffect(() => {
    if (!orgId) return;
    const stored = loadSetupWatch();
    if (stored?.orgId !== orgId) return;
    if (Math.max(0, Date.now() - stored.startedAt) >= INTERIM_READY_MS) {
      clearSetupWatch();
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- rehydration once per settled org; the functional update never clobbers a live watch
    setSettingUp((prev) => prev ?? { productKey: stored.productKey, startedAt: stored.startedAt });
  }, [orgId]);

  // Interim, until the status service ships: with no URL to poll, mimic the wait with a
  // fixed timer so the card holds "setting up" through the typical build instead of
  // offering Open immediately. Only for products that actually provision — an instant
  // product's trial is usable the moment it registers. Falls away untouched once a real
  // URL is registered.
  const interimActive = !!settingUp && !watchStatusUrl && productIsProvisioned(settingUp.productKey);
  const interimReady = useInterimReady(interimActive ? settingUp.productKey : null, settingUp?.startedAt);
  let watchState = readiness.state;
  if (interimActive) watchState = interimReady ? 'ready' : 'checking';

  // The status API's ready comes from an in-estate probe, which can beat the customer's own
  // path (public DNS, edge certificate) by minutes on a fresh org — so gate Open on the
  // browser's probe of the org host, and only where the open target IS the org host.
  const setupGateHost = orgHostToGate(settingUp, orgSlug);
  const setupHostReady = useHostReady(setupGateHost);
  if (watchState === 'ready') watchState = gateOnHost(setupGateHost, setupHostReady);

  // Celebrate when a provisioned product actually becomes ready — trial-start only
  // begins a wait, so the confetti fires when the wait ends. Instant products celebrate
  // at trial-start instead (useStartTrial), so they are excluded here even if they ever
  // gain a status URL — one celebration per trial, never two.
  const readyProduct = watchState === 'ready' && settingUp && productIsProvisioned(settingUp.productKey)
    ? settingUp.productKey
    : null;
  useEffect(() => {
    if (!readyProduct) return;
    void import('canvas-confetti')
      .then(({ default: confetti }) =>
        confetti({ particleCount: 140, spread: 75, origin: { y: 0.7 }, disableForReducedMotion: true }))
      .catch(() => {});
  }, [readyProduct]);

  const setup: SetupWatch = {
    productKey: settingUp?.productKey ?? null,
    state: watchState,
    stages: readiness.stages,
    watch,
    dismiss,
  };

  const orgAdmin = isOrgAdmin(auth.permissions);
  const roles = auth.roles ?? null;

  const trialIntent = useTrialIntentAutoStart({
    me: state.kind === 'ready' ? state.data : null,
    watch,
    refetch: load,
  });

  const sections = useMemo(
    () =>
      state.kind === 'ready'
        ? resolveProductSections(state.data.products, orgAdmin)
        : null,
    [state, orgAdmin],
  );

  return (
    <EnterpriseHubView status={state.kind} >
      <Center w={'100%'} flex={1} style={{ overflow: 'hidden', zIndex:1 }}>
        <Stack w={'100%'} mah={'100%'} align="center" gap={0} style={{ overflowY: 'auto' }} py={24}>
          <SectionHeader
            title={auth.user?.firstName ? `Welcome back, ${auth.user.firstName}` : 'Welcome back'}
            description="Choose an application to continue."
          />
          <Stack
            gap="xl"
            maw={900}
            mx="auto"
            mt={'xl'}
            style={{ width: '100%', position: 'relative', zIndex: 1 }}
          >
            <TrialIntentNotice
              prompt={trialIntent.prompt}
              busy={trialIntent.busy}
              error={trialIntent.error}
              onStart={trialIntent.start}
              onDismiss={trialIntent.dismiss}
            />
            <WaitingRequestsBanner />
            <Outlet context={{ state, sections, refetch: load, isOrgAdmin: orgAdmin, orgSlug, roles, setup, trialIntentBusy: trialIntent.busy, trialStarted: trialIntent.manualTrialStarted }} />
          </Stack>
        </Stack>
      </Center>
    </EnterpriseHubView>
  );

}
